import { ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import OutsideClickHandler from 'src/components/OutsideClickHandler';

export interface ITrackViewOption {
    value: string;
    label: string;
}

interface IProps {
    options: ITrackViewOption[];
    selectedValue: string | null;
    onSelect: (value: string) => void;
    dropdownPosition?: 'left' | 'right';
    // Class on the outermost OutsideClickHandler wrapper. Pass e.g. "relative flex min-w-0
    // max-w-full" so the trigger can flex/shrink and a truncating labelContent gets its
    // ellipsis from the available width.
    containerClassName?: string;
    // Extra classes on the trigger row (label + chevron).
    triggerClassName?: string;
    listItemClassName?: string;
    // Custom trigger content rendered in place of the selected label (the chevron is still
    // appended). A render function receives the open state so callers can e.g. disable a
    // truncate tooltip while the menu is open.
    labelContent?: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
}

/**
 * Lightweight "title as a dropdown" used by the Overview card header on the Track map view
 * to switch between Overview (no saved view) and the user's saved views. Ported from WAL's
 * OrderDetailsWidgets/FilterDropdown.tsx — only the props Track needs were kept.
 */
const TrackViewDropdown: React.FC<IProps> = ({
    options,
    selectedValue,
    onSelect,
    dropdownPosition = 'right',
    containerClassName = 'relative inline-block',
    triggerClassName = '',
    listItemClassName = '',
    labelContent,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectedLabel = options.find((opt) => opt.value === selectedValue)?.label ?? '';

    const handleSelect = (value: string) => {
        onSelect(value);
        setIsOpen(false);
    };

    return (
        <OutsideClickHandler onOutsideClick={() => setIsOpen(false)} containerClassName={containerClassName}>
            <div className={`flex items-center gap-1.5 cursor-pointer ${triggerClassName}`} onClick={() => setIsOpen((prev) => !prev)}>
                {labelContent ? typeof labelContent === 'function' ? labelContent(isOpen) : labelContent : <p className="text-xs leading-[18px] font-normal text-grayLight900">{selectedLabel}</p>}
                <ChevronDown className={`w-4 h-4 text-gray500 transition-transform flex-none ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            {isOpen && (
                <div className={`absolute ${dropdownPosition === 'left' ? 'left-0' : 'right-0'} top-full mt-1.5 bg-white border border-utilityGray200 rounded-xl shadow-lg z-10 min-w-[200px]`}>
                    <ul className="p-2">
                        {options.map((option) => (
                            <li
                                key={option.value}
                                className={`px-2.5 py-2 text-xs leading-5 text-grayLight900 hover:bg-gray50 rounded-md cursor-pointer truncate ${
                                    selectedValue === option.value ? 'bg-gray50 font-medium' : 'font-normal'
                                } ${listItemClassName}`}
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </OutsideClickHandler>
    );
};

export default TrackViewDropdown;
